import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

/* Notifications sur le téléphone : abonnement de cet appareil au compte connecté. */

export type PushState =
  | "loading"
  /** Navigateur sans notifications (ou application lancée en mode développement). */
  | "unsupported"
  /** iPhone : il faut d'abord installer l'application sur l'écran d'accueil. */
  | "needs-install"
  /** Notifications refusées dans les réglages du téléphone. */
  | "denied"
  | "off"
  | "on";

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isInstalled(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = (value + "=".repeat((4 - (value.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

/** Abonnement de cet appareil, s'il existe. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  try {
    const reg = await registration();
    return (await reg?.pushManager?.getSubscription()) ?? null;
  } catch {
    return null;
  }
}

/** À la déconnexion : ce téléphone ne reçoit plus les notifications de ce compte. */
export async function forgetPushSubscription(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api("/push/subscriptions", { method: "DELETE", body: { endpoint: sub.endpoint } }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>("loading");

  const refresh = useCallback(async () => {
    const supported = "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
    if (!supported) {
      setState(isIos() && !isInstalled() ? "needs-install" : "unsupported");
      return;
    }
    if (!(await registration())) {
      setState("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    setState((await currentSubscription()) ? "on" : "off");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Demande l'autorisation puis abonne ce téléphone. Doit suivre un appui de l'utilisateur. */
  const enable = useCallback(async () => {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      setState(permission === "denied" ? "denied" : "off");
      return false;
    }
    const reg = await registration();
    if (!reg) throw new Error("Application non installée : rechargez la page puis réessayez.");
    const { publicKey } = await api<{ publicKey: string }>("/push/key");
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) });
    const json = sub.toJSON();
    await api("/push/subscriptions", { method: "POST", body: { endpoint: json.endpoint, keys: json.keys } });
    setState("on");
    return true;
  }, []);

  const disable = useCallback(async () => {
    await forgetPushSubscription();
    setState("off");
  }, []);

  const test = useCallback(() => api<{ sent: number }>("/push/test", { method: "POST" }), []);

  return { state, enable, disable, test };
}
